import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Button } from 'reactstrap';
import { GlobalContext } from '../api/GlobalContext';
import { useWakeLock } from 'react-screen-wake-lock';
import HashState from '../variables/hash';
import MicDropStatus from '../api/MicDropStatus';
import Modal from '../components/Modals/Modal';
import { createToggleRecordingTool } from '../components/Misc/Synapse/ConversationDetailsTools.js';
const MAX_RECORD_TIME = 2; // 2 Hours

// TODO:
// Check Rollover at 2 min
// Cap and Max Record Time
const MAX_UPLOAD_SIZE = 5 * 1024 * 1024; // 5MB in bytes (With my airpods on it came out to ~1MB per minute)
const MAX_WHISPER_UPLOAD_SIZE = 20 * 1024 * 1024;
const MIN_AUDIO_UPLOAD_DURATION_MS = 1000;

function getMicButtonLabel(label) {
  const cleanLabel = label.replace(/^Default - /, '').trim();
  if (!cleanLabel) {
    return 'Mic';
  }
  if (/^no microphone found$/i.test(cleanLabel)) {
    return 'No mic found';
  }
  if (
    /\b(built[- ]?in|internal|integrated|microphone array)\b/i.test(cleanLabel) ||
    /\bmacbook\b/i.test(cleanLabel)
  ) {
    return 'Built-in Mic';
  }
  return cleanLabel.replace(/\bmicrophone\b/gi, 'Mic');
}

//API to handle audio recording
var blobLength = 0;
function sendNativeMessage(type, value = {}) {
  const message = {
    type: type,
    value: value,
  };
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
    return;
  }
  // When running inside the native wrapper, the CX embed UI is often inside a cross-origin iframe.
  // In that case, `window.ReactNativeWebView` may not be present in the iframe context.
  // We relay to the parent page (loader.js) which forwards to ReactNativeWebView.
  try {
    if (
      window.parent &&
      window.parent !== window &&
      window.parent.postMessage
    ) {
      window.parent.postMessage({ __compNativeRelay: true, message }, '*');
    }
  } catch (_) {}
}

// Never record from an iPhone (Continuity) mic unless it was picked by hand:
// macOS can make it the system default when the phone is nearby.
const isIPhoneMic = (d) => /iphone/i.test(d?.label || '');
export function effectiveMicId(selectedId, devices = []) {
  if (selectedId && selectedId !== 'default') return selectedId;
  const def = devices.find((d) => d.deviceId === 'default');
  if (!def || !isIPhoneMic(def)) return selectedId;
  const fallback = devices.find(
    (d) =>
      d.deviceId !== 'default' &&
      d.deviceId !== 'communications' &&
      !isIPhoneMic(d) &&
      !/virtual/i.test(d.label),
  );
  return fallback ? fallback.deviceId : selectedId;
}

var audioRecorder = {
  /** Stores the recorded audio as Blob objects of audio data as the recording continues*/
  audioBlobs: [] /*of type Blob[]*/,
  /** Stores the reference of the MediaRecorder instance that handles the MediaStream when recording starts*/
  mediaRecorder: null /*of type MediaRecorder*/,
  /** Stores the reference to the stream currently capturing the audio*/
  streamBeingCaptured: null /*of type MediaStream*/,
  /** Start recording the audio
   * @returns {Promise} - returns a promise that resolves if audio recording successfully started
   */
  start: function (stream) {
    return new Promise((resolve, reject) => {
      try {
        //save the reference of the stream to be able to stop it when necessary
        audioRecorder.streamBeingCaptured = stream;
        //create a media recorder instance by passing that stream into the MediaRecorder constructor
        audioRecorder.mediaRecorder = new MediaRecorder(
          stream,
        ); /*the MediaRecorder interface of the MediaStream Recording
        API provides functionality to easily record media*/

        //clear previously saved audio Blobs, if any
        const blobs = [];
        audioRecorder.audioBlobs = blobs;

        //add a dataavailable event listener in order to store the audio data Blobs when recording
        audioRecorder.mediaRecorder.addEventListener(
          'dataavailable',
          (event) => {
            //store audio Blob object
            blobs.push(event.data);
            blobLength += 1;
          },
        );

        //start the recording by calling the start method on the media recorder
        // Crazy, but the start(1000) is necessary for apple devices for some reason...
        audioRecorder.mediaRecorder.start(1000); // This starts recording and sends out small chunks of data every second. This allows you to process or save the data continuously, and helps manage memory better during the recording.
        resolve(); // resolved when started
      } catch (error) {
        reject(error);
      }
    });
  },
  /** Stop the started audio recording
   * @returns {Promise} - returns a promise that resolves to the audio as a blob file
   */
  stop: function () {
    //return a promise that would return the blob or URL of the recording
    return new Promise((resolve) => {
      //save audio type to pass to set the Blob type
      let mimeType = audioRecorder.mediaRecorder.mimeType;
      const blobs = audioRecorder.audioBlobs;

      //listen to the stop event in order to create & return a single Blob object
      audioRecorder.mediaRecorder.addEventListener('stop', () => {
        //create a single blob object, as we might have gathered a few Blob objects that needs to be joined as one
        // console.log(len(audioRecorder.audioBlobs));
        let audioBlob = new Blob(blobs, { type: mimeType });

        //resolve promise with the single audio blob representing the recorded audio
        resolve(audioBlob);
      });
      audioRecorder.cancel();
    });
  },
  /** Cancel audio recording*/
  cancel: function () {
    //stop the recording feature
    audioRecorder.mediaRecorder.stop();

    //stop all the tracks on the active stream in order to stop the stream
    audioRecorder.stopStream();

    //reset API properties for next recording
    audioRecorder.resetRecordingProperties();
  },
  /** Stop all the tracks on the active stream in order to stop the stream and remove
   * the red flashing dot showing in the tab
   */
  stopStream: function () {
    //stopping the capturing request by stopping all the tracks on the active stream
    audioRecorder.streamBeingCaptured
      .getTracks() //get all tracks from the stream
      .forEach((track) /*of type MediaStreamTrack*/ => track.stop()); //stop each one
  },
  /** Reset all the recording properties including the media recorder and stream being captured*/
  resetRecordingProperties: function () {
    audioRecorder.mediaRecorder = null;
    audioRecorder.streamBeingCaptured = null;

    /*No need to remove event listeners attached to mediaRecorder as
        If a DOM element which is removed is reference-free (no references pointing to it), the element itself is picked
        up by the garbage collector as well as any event handlers/listeners associated with it.
        getEventListeners(audioRecorder.mediaRecorder) will return an empty array of events.*/
  },
};

// Global variables
let sendChunkIntervalID = null;
let lastSendTime = 0;
// Recorder currently holding the mic
let activeRecorder = null;

// Native builds without per-chunk visit IDs could upload the last chunk to the next visit
export function canSwitchRecordingVisits() {
  try {
    const platform = sessionStorage.getItem('platform');
    if (platform !== 'android' && platform !== 'ios') return true;
    return sessionStorage.getItem('nativeVisitSwitch') === 'true';
  } catch (_) {
    return false;
  }
}
/*We aim to have notes ready within 20 seconds after stopping. Whisper takes 10-15 seconds to 
transcribe every 2 minutes of audio, plus another 10-15 seconds for AI responses, 
totaling 20-30 seconds. Shortening chunk size might compromise context accuracy. 
Our rate limit used to be 3 calls per minute but has increased to 500. With 2-minute chunks, 
we can handle up to 1000 users concurrently. Extending chunks to 4 minutes could double 
this capacity. We plan to build GPU-powered Whisper Machines for better performance and efficiency.*/

let chunkInterval = 2 * 60 * 1000; // 2 minutes

const AudioRecorder = forwardRef(function AudioRecorder(props, ref) {
  // Action wording. Defaults keep every existing call site on Record/Resume;
  // views that frame recording as a visit pass their own.
  const startLabel = props.startLabel || 'Record';
  const resumeLabel = props.resumeLabel || 'Resume';
  // In CX-embed, the app is often loaded in an iframe and receives platform via query param.
  // Normalize it into sessionStorage so the rest of the recorder logic can branch consistently.
  const platform = (() => {
    try {
      // Prefer comp_platform from URL (works in CX embed iframe).
      // Use full href to handle cases where routing moves params into the hash or drops search.
      const href = String(
        window.location && window.location.href ? window.location.href : '',
      );
      const m = href.match(/(?:[?#&])comp_platform=([^&#]+)/);
      const fromQuery = m && m[1] ? decodeURIComponent(m[1]) : null;
      if (fromQuery && fromQuery !== '') {
        sessionStorage.setItem('platform', fromQuery);
        return fromQuery;
      }
      const existing = sessionStorage.getItem('platform');
      if (existing && existing !== '') return existing;
      return existing;
    } catch (_) {
      return null;
    }
  })();
  // alert(`platform = "${platform}"`)
  const [startTime, setStartTime] = useState(null);
  const [wordChangeCount, setWordChangeCount] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const audioContextRef = useRef(null);
  const audioAnalyserRef = useRef(null); // Single ref for audio analysis
  const speakingTimeoutRef = useRef(null);
  const [micDevices, setMicDevices] = useState([]);
  const [selectedMicId, setSelectedMicId] = useState(() => {
    try {
      return localStorage.getItem('comprehendpt_selectedMicId') || '';
    } catch (e) {
      return '';
    }
  });

  /*
    props:
        conversationID
        refresh - Function to call to refresh the conversation/transcript
        setStatus - Place for us to keep the user up to date on the status of the recording

    returns:
        A button that will start recording audio and send it to the server
            Setting status along the way
            And updating the transcript when it is done
    */
  // Arguments
  if (props.conversationID == null) {
    console.log('AudioRecorder: props.conversationID prop is null');
  }
  if (props.refresh == null) {
    console.log('AudioRecorder: props.refresh prop is null');
  }
  if (props.setStatus == null) {
    console.log('AudioRecorder: props.setStatus prop is null');
  }

  // Globals
  const { apiClient } = useContext(GlobalContext);
  const { isSupported, released, request, release } = useWakeLock();
  const isUploadingQueueRef = useRef(false);
  const audioQueueRef = useRef([]);
  const elapsedTimeTimerRef = useRef(null);
  const hash = HashState();

  // Conversation ID Mirror, if we don't use a mirror, the conversationID will be null when we try
  // to upload the audio after the component unmounts (When we click see all while still recording.)
  // const [conversationID, setConversationID] = useState(props.conversationID);
  const conversationIDRef = useRef(props.conversationID);
  const conversationIdsRef = useRef(props.conversationIds || []);
  // Multi-recorder continuity: backend uses this to attribute the *start* of the next chunk correctly
  // when the patient name was only mentioned near the end of the prior chunk.
  const previousLastSpeakerRef = useRef(null);
  const lastConversationIdsRef = useRef(props.conversationIds || []);
  // props.freezeChunkTargets: targets frozen when a chunk starts, so audio goes to who was in the recording while it was spoken.
  const chunkTargetsRef = useRef(null);
  const retargetTimerRef = useRef(null);
  // We update the conversationIDRef whenever we click start recording, so that we can use it when we unmount or when we click new convo
  // Determine if we should show "Resume" or "Record" based on seconds transcribed in the conversation
  const [hasStartedRecording, setHasStartedRecording] = useState(
    props.seconds_transcribed !== 0,
  ); // This is used to check if the user has started recording

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (showDropdown && !event.target.closest('.dropdown-container')) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  // sendChunk restarts getUserMedia from a closure, so it reads the mic from a ref.
  const selectedMicIdRef = useRef(selectedMicId);
  selectedMicIdRef.current = selectedMicId;
  const micDevicesRef = useRef(micDevices);
  micDevicesRef.current = micDevices;
  // COM-491: the iPhone-mic fallback ships behind visits_schedule.
  const micIdFor = (id, devices) =>
    apiClient.features?.visits_schedule === true
      ? effectiveMicId(id, devices)
      : id;

  // useEffect to persist the selected microphone ID
  useEffect(() => {
    try {
      localStorage.setItem('comprehendpt_selectedMicId', selectedMicId);
    } catch (e) {
      console.log('Unable to persist mic selection', e);
    }
  }, [selectedMicId]);

  const refreshMicDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter((d) => d.kind === 'audioinput');
      const mapped = audioInputs.map((device, index) => {
        const rawLabel =
          device.label && device.label.trim().length > 0
            ? device.label
            : device.deviceId === 'default'
              ? 'System default microphone'
              : `Microphone ${index + 1}`;
        return {
          deviceId: device.deviceId,
          // Chrome (esp. on Windows) prefixes the default device with
          // "Default - "; strip it so we show just the device name.
          label: rawLabel.replace(/^Default - /, ''),
        };
      });

      setMicDevices(mapped);

      const defaultDevice = mapped.find((d) => d.deviceId === 'default');
      const hasSelected =
        selectedMicId && mapped.some((d) => d.deviceId === selectedMicId);

      // If selected device disappeared, fall back to system default or first available
      if (selectedMicId && !hasSelected) {
        if (defaultDevice) {
          setSelectedMicId('default');
        } else if (mapped.length > 0) {
          setSelectedMicId(mapped[0].deviceId);
        } else {
          setSelectedMicId('');
        }
        return;
      }

      // If nothing selected yet, prefer system default; otherwise pick first available
      if (!selectedMicId && mapped.length > 0) {
        if (!defaultDevice) {
          setSelectedMicId(mapped[0].deviceId);
        }
      }
    } catch (err) {
      console.log('Unable to enumerate microphones', err);
    }
  }, [selectedMicId]);

  useEffect(() => {
    refreshMicDevices();

    const handleDeviceChange = () => refreshMicDevices();
    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener(
        'devicechange',
        handleDeviceChange,
      );
    }
    return () => {
      if (navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener(
          'devicechange',
          handleDeviceChange,
        );
      }
    };
  }, [refreshMicDevices]);

  const buildMicAudioConstraints = () => {
    const audioConstraints = {
      echoCancellation: false,
      noiseSuppression: false,
      sampleRate: 44100,
      autoGainControl: false,
    };

    const micId = micIdFor(selectedMicIdRef.current, micDevicesRef.current);
    if (micId && micId !== 'default') {
      audioConstraints.deviceId = { exact: micId };
    }

    return { audio: audioConstraints };
  };

  // Use useEffect to update the state when props.seconds_transcribed changes
  useEffect(() => {
    setHasStartedRecording(props.seconds_transcribed !== 0);
  }, [props.seconds_transcribed]); // This effect will run whenever props.seconds_transcribed changes
  useEffect(() => {
    // Keep refs aligned with latest props so upload uses current selections
    const nextConversationIds = props.conversationIds || [];
    conversationIdsRef.current = nextConversationIds;
    // If the selection changed, reset continuity so we don't bias attribution toward a patient
    // that may no longer be in the selected set.
    const prevConversationIds = lastConversationIdsRef.current || [];
    const changed =
      prevConversationIds.length !== nextConversationIds.length ||
      prevConversationIds.some((id, i) => id !== nextConversationIds[i]);
    if (changed) {
      previousLastSpeakerRef.current = null;
      lastConversationIdsRef.current = nextConversationIds;
    }

    // Keep native recorder in sync with latest targets
    if (platform === 'android' || platform === 'ios') {
      sendNativeMessage('convoIDs', nextConversationIds);
    }

    // In addition, whenever we change the convoersation IDs, we should trigger a chunk if time is >5 seconds
    if (nextConversationIds.length > 0 && isRecordingRef.current) {
      const currentTime = new Date();
      const elapsedTime = currentTime - lastSendTime;
      clearTimeout(retargetTimerRef.current);
      if (elapsedTime >= 5000) {
        sendChunk();
      } else if (props.freezeChunkTargets) {
        // Too young to cut now; cut once it reaches 5s instead of riding to the 2 min mark.
        retargetTimerRef.current = setTimeout(() => {
          if (isRecordingRef.current) sendChunk();
        }, 5000 - elapsedTime);
      }
    }
  }, [platform, props.conversationIds]);

  // View Manager
  const [isRecording, setIsRecording] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const isRecordingRef = useRef(isRecording);
  // Set true when the user explicitly clicks Stop, checked in sendChunk's stop→start gap
  // to prevent a stop click that lands during chunk rollover from being ignored (ghost recording).
  const stopRequestedRef = useRef(false);
  // We use a reference to the isRecording state because when the component unmounts, the real
  // isRecording will be reset to false, but we need to know if we are recording when we unmount
  // so we can stop the recording
  // Update ref whenever isRecording changes
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  // Detect mobile/tablet/touch-first devices so we can hide desktop-only controls
  useEffect(() => {
    const detectMobile = () => {
      if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
      }

      const uaData = navigator.userAgentData;
      const ua = navigator.userAgent || navigator.vendor || window.opera || '';
      const hasTouch =
        'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0;
      const coarsePointer =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(pointer: coarse)').matches;

      const isIpadOS =
        navigator.platform === 'MacIntel' &&
        (navigator.maxTouchPoints || 0) > 1;
      const isMobileUA =
        /android|iphone|ipod|ipad|windows phone/i.test(ua) ||
        (uaData && uaData.mobile);
      const isAndroidTablet = /android/i.test(ua) && !/mobile/i.test(ua); // tablets often omit "mobile"
      const isSmartDisplay = /CrKey|Nest Hub/i.test(ua); // smart displays (Nest Hub, etc.)

      return (
        isMobileUA ||
        isIpadOS ||
        isAndroidTablet ||
        isSmartDisplay ||
        (hasTouch && coarsePointer)
      );
    };

    const updateIsMobile = () => setIsMobile(detectMobile());
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  useEffect(() => {
    if (!props.isRecording && isRecording) {
      stopButton(); // Stop recording if the parent indicates it should stop
    }
  }, [props.isRecording]);

  // Create a single ref to hold all action handlers for external access (tools, etc)
  const actionsRef = useRef({});

  const [isUploading, setIsUploading] = useState(false);
  const setElapsedTime = (newTime) => {
    props.setStatus('Recording: ' + newTime);
  };

  const audioElementRef = useRef(null);
  // const audioElementSourceRef = useRef(null);

  /** Start the audio recording*/
  function startAudioRecording() {
    // console.log("Updating conversationID to " + props.conversationID)
    conversationIDRef.current = props.conversationID;
    const chunkIds = [...(conversationIdsRef.current || [])];
    chunkTargetsRef.current = props.freezeChunkTargets
      ? {
          id:
            conversationIDRef.current ??
            (chunkIds.length === 1 ? chunkIds[0] : null),
          ids: chunkIds,
        }
      : null;
    // Reset last-speaker context for new recording session (first chunk has no previous chunk)
    previousLastSpeakerRef.current = null;
    setIsRecording(true);

    const targets = conversationIdsRef.current || [];
    const isMobile = platform === 'android' || platform === 'ios';
    if (isMobile) {
      sendNativeMessage('sessionKey', apiClient.sessionKey);
      sendNativeMessage('sessionEmail', apiClient.sessionEmail);
      sendNativeMessage('convoID', conversationIDRef.current);
      sendNativeMessage('convoIDs', targets);
      sendNativeMessage('startRecording', {
        multirecordContext: Boolean(props.multirecordContext),
      });
      return;
    }

    // console.log("Recording Audio Chunk...");
    //If a previous audio recording is processing, pause it
    // let recorderAudioIsProcessing = !audioElementRef.paused; // the paused property tells whether the media element is paused or not
    // console.log("paused?", !recorderAudioIsProcessing);
    // if (recorderAudioIsProcessing) {
    //     audioElementRef.pause();
    //     //also hide the audio processing indicator displayed on the screen
    // }

    //Feature is not supported in browser
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      props.setStatus(
        'Your browser does not support microphone access. Please try using Chrome or Firefox.',
      );
      setIsRecording(false);
      return;
    }
    //start recording using the audio recording API
    navigator.mediaDevices
      .getUserMedia(
        buildMicAudioConstraints() /*of type MediaStreamConstraints*/,
      )
      //returns a promise that resolves to the audio stream
      .then((stream) /*of type MediaStream*/ => {
        // Stop landed while we were waiting for getUserMedia — release the stream
        // before allocating AudioContext/analyser/monitorAudio loop.
        if (stopRequestedRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        // Use the same stream for recording and audio visualization
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
        const microphone =
          audioContextRef.current.createMediaStreamSource(stream);
        audioAnalyserRef.current = audioContextRef.current.createAnalyser();
        microphone.connect(audioAnalyserRef.current);

        monitorAudio(); // Start visual/audio monitoring

        // Start MediaRecorder using same stream
        return audioRecorder.start(stream);
      })
      .then(() => {
        if (stopRequestedRef.current) {
          if (audioRecorder.mediaRecorder) audioRecorder.cancel();
          setIsRecording(false);
          return;
        }
        // Set an interval to call sendChunk every 5 minutes
        // setInterval(sendChunk, 5*60 * 1000);
        // if (sendChunkIntervalID != null) {
        //     clearInterval(sendChunkIntervalID);
        // }
        sendChunkIntervalID = setInterval(checkConditionsAndSendChunk, 1000); // 2 minutes or 20 MB
        //store the recording start time to display the elapsed time according to it
        lastSendTime = new Date();
      })
      .catch((error) => {
        console.error('Error starting audio recording:', error);
        //on error
        //No Browser Support Error
        if (
          error.message.includes(
            'mediaDevices API or getUserMedia method is not supported in this browser.',
          )
        ) {
          console.log('To record audio, use browsers like Chrome and Firefox.');
          console.log(error.message);
        }

        //Error handling structure
        switch (error.name) {
          case 'AbortError': //error from navigator.mediaDevices.getUserMedia
            console.log('An AbortError has occured.');
            break;
          case 'NotAllowedError': //error from navigator.mediaDevices.getUserMedia
            console.log(
              'A NotAllowedError has occured. User might have denied permission.',
            );
            break;
          case 'NotFoundError': //error from navigator.mediaDevices.getUserMedia
            console.log('A NotFoundError has occured.');
            break;
          case 'NotReadableError': //error from navigator.mediaDevices.getUserMedia
            console.log('A NotReadableError has occured.');
            break;
          case 'SecurityError': //error from navigator.mediaDevices.getUserMedia or from the MediaRecorder.start
            console.log('A SecurityError has occured.');
            break;
          case 'TypeError': //error from navigator.mediaDevices.getUserMedia
            console.log('A TypeError has occured.');
            break;
          case 'InvalidStateError': //error from the MediaRecorder.start
            console.log('An InvalidStateError has occured.');
            break;
          case 'UnknownError': //error from the MediaRecorder.start
            console.log('An UnknownError has occured.');
            break;
          default:
            console.log('An error occured with the error name ' + error.name);
        }
        props.setStatus('Error with Recorder, Try another browser');
      });
  }

  async function checkConditionsAndSendChunk() {
    const currentTime = new Date();
    const elapsedTime = currentTime - lastSendTime;
    const isSizeExceeded = blobLength >= MAX_WHISPER_UPLOAD_SIZE;

    if (elapsedTime >= chunkInterval || isSizeExceeded) {
      // 2 minutes or 20 MB
      clearInterval(sendChunkIntervalID);
      await sendChunk();
      // For some reason this makes the audio duration 1s when it should be 120s, the rollover duration
      // This ends up being a ref assignment not a value assignment thus current time and last send time will be the same
      // Regardless it's redundant logic so let's remove it.
      // lastSendTime = currentTime;
    }
  }
  /**
   * Sends the current audio chunk by stopping the recording, uploading it, and then restarting the recording.
   *
   * This function first stops the ongoing audio recording to ensure that the current audio chunk is fully captured and saved.
   * The stopAudioRecording function is asynchronous, meaning it will wait for the recording to stop completely before proceeding.
   * However, while stopAudioRecording waits for the recording to stop, it does not wait for the uploadAudio function to finish
   * before starting the next recording. As a result, startAudioRecording may be triggered while the previous audio chunk is still
   * being uploaded, demonstrating the asynchronous nature of both stopAudioRecording and uploadAudio.
   */
  async function sendChunk() {
    console.log('Sending Audio Chunk...');
    // We need to stop the recording, make sure it is fully read via IO then start recording again
    await stopAudioRecording();
    if (stopRequestedRef.current) return;
    startAudioRecording();
    // setTimeout(() => {
    //     startAudioRecording();
    // }, 10); // 10ms delay
  }

  let frameCount = 0; // Counter to control the frequency of checks
  let animationFrameId = null; // Store the animation frame ID

  const demonitorAudio = () => {
    // Cancel the animation frame if it exists
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    // Clear any pending speaking timeout
    if (speakingTimeoutRef.current) {
      clearTimeout(speakingTimeoutRef.current);
      speakingTimeoutRef.current = null;
    }

    // Reset states
    setIsSpeaking(false);
    setWordChangeCount(0);
    frameCount = 0;
  };

  const monitorAudio = () => {
    const analyser = audioAnalyserRef.current;
    const dataArray = new Uint8Array(analyser.fftSize);

    const checkAudio = () => {
      frameCount++;
      if (frameCount % 10 === 0) {
        // Adjust this value to change the frequency (10 means every 10 frames)

        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += Math.abs(dataArray[i] - 128);
        }

        const average = sum / dataArray.length;
        const threshold = 1.2;
        const isSpeakingDetected = average > threshold;

        if (isSpeakingDetected) {
          setWordChangeCount((prevCount) => prevCount + 1);

          if (speakingTimeoutRef.current) {
            clearTimeout(speakingTimeoutRef.current);
            speakingTimeoutRef.current = null;
          }

          setIsSpeaking(true);
        } else if (!speakingTimeoutRef.current) {
          // Wait 1 second before turning off
          speakingTimeoutRef.current = setTimeout(() => {
            setIsSpeaking(false);
            speakingTimeoutRef.current = null;
          }, 5000); // delay after silence
        }
      }

      animationFrameId = requestAnimationFrame(checkAudio);
    };

    checkAudio();
  };

  function stopAudioRecording() {
    const recordedDurationMs =
      lastSendTime instanceof Date ? new Date() - lastSendTime : 0;

    // Stop audio monitoring
    demonitorAudio();

    if (audioAnalyserRef.current) {
      audioAnalyserRef.current.disconnect();
      audioAnalyserRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    clearInterval(sendChunkIntervalID);

    //stop the recording using the audio recording API
    return audioRecorder
      .stop()
      .then((audioAsblob) => {
        if (
          recordedDurationMs < MIN_AUDIO_UPLOAD_DURATION_MS ||
          audioAsblob.size === 0
        ) {
          console.log(
            `Skipping too-short audio recording (${recordedDurationMs}ms, ${audioAsblob.size} bytes).`,
          );
          setIsUploading(false);
          return;
        }
        audioQueueRef.current.push({
          audioBlob: audioAsblob,
          retryCount: 0,
          duration: parseInt(recordedDurationMs / 1000) + 1,
          targets: chunkTargetsRef.current,
        });
        // Play recorder audio
        // playAudio(audioAsblob);
        if (!isUploadingQueueRef.current) {
          processQueue(); // Start processing the queue if it's not already running
        }
      })
      .catch((error) => {
        setIsUploading(false);
        console.error('Error stopping audio recording:', error);
        //Error handling structure
        switch (error.name) {
          case 'InvalidStateError': //error from the MediaRecorder.stop
            console.log('An InvalidStateError has occured.');
            break;
          default:
            console.log('An error occured with the error name ' + error.name);
        }
      });
  }

  const [showModal, setShowModal] = useState(false);
  const handleCancel = () => {
    setShowModal(false);
  };

  let dingTimeout;

  function playDing() {
    if (dingTimeout) return; // Prevent further calls until the timeout is cleared

    // Set the timeout flag immediately to prevent multiple calls
    dingTimeout = true;
    setShowModal(true);
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // 880 Hz = ding sound
    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime); // Set volume

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start();

    // Stop oscillator and close context after the ding
    setTimeout(() => {
      oscillator.stop();
      audioContext.close();
    }, 200); // Stop after 200ms for a short "ding"

    // Set a timeout to allow the next ding
    setTimeout(() => {
      console.log('Ding timeout cleared, ready for next ding');
      dingTimeout = false;
    }, 60000); // 60 second cooldown
  }

  function processQueue() {
    if (isUploadingQueueRef.current) {
      console.log('Queue processing is already in progress.');
      return; // Exit if already processing the queue
    }

    isUploadingQueueRef.current = true;
    console.log('Starting queue processing...');

    async function processNext() {
      if (audioQueueRef.current.length === 0) {
        console.log('Queue is empty. Stopping queue processing.');
        isUploadingQueueRef.current = false;
        // props.setStatus('Transcription Complete!');
        return; // Exit if the queue is empty
      }

      const { audioBlob, retryCount, duration, targets } =
        audioQueueRef.current.shift(); // Get the next audio blob in the queue with its retry count
      console.log(
        `Processing audio blob. Queue length: ${audioQueueRef.current.length}, Retry count: ${retryCount}`,
      );

      try {
        await uploadAudio(audioBlob, duration, targets);
        console.log('Audio upload successful.');
        // Continue processing the queue after successful upload
        processNext();
      } catch (error) {
        console.error('Error uploading audio:', error);
        const errorMessage =
          typeof error === 'string' ? error : JSON.stringify(error);

        if (errorMessage.includes('Invalid audio format')) {
          console.error('Invalid audio format detected. Skipping item.');
          playDing();
          // Do not re-add the item to the queue
          processNext(); // Continue processing the queue
        } else if (retryCount < 3) {
          console.log(
            `Re-adding the failed item to the front of the queue. Retry attempt ${retryCount + 1}`,
          );
          audioQueueRef.current.unshift({
            audioBlob,
            retryCount: retryCount + 1,
            duration,
            targets,
          }); // Re-add the failed item with incremented retry count
          // Ensure flag is reset to allow retry
          isUploadingQueueRef.current = false;

          // Check if the internet is online before retrying
          if (navigator.onLine) {
            processQueue(); // Restart processing the queue
          } else {
            console.log(
              'No internet connection. Waiting for internet to be restored.',
            );
            // Listen for the online event and retry when the connection is restored
            window.addEventListener(
              'online',
              () => {
                console.log('Internet connection restored.');
                processQueue(); // Restart processing the queue when the internet is back
              },
              { once: true },
            ); // Only listen once
          }
        } else {
          console.error('Max retry attempts reached. Skipping the item.');
          playDing();
          processNext(); // Move to the next item
        }
      }
    }

    processNext(); // Start processing the queue
  }

  // add a message handler for the native recorder to call for uploading
  useEffect(() => {
    const handleInbound = (raw) => {
      const message = JSON.parse(raw);
      if (message.type === 'setStatus') {
        props.setStatus(message.value);
      } else if (message.type === 'refresh') {
        props.refresh();
        console.log('Refreshing');
      } else if (message.type === 'audioActivity') {
        // Handle audio activity data from native recorder for wave visualization
        const {
          isSpeaking: nativeIsSpeaking,
          wordChangeCount: nativeWordChangeCount,
        } = message.value || {};
        if (nativeIsSpeaking !== undefined) {
          setIsSpeaking(nativeIsSpeaking);
        }
        if (nativeWordChangeCount !== undefined) {
          setWordChangeCount(nativeWordChangeCount);
        }
      } else if (message.type === 'playDing') {
        playDing();
      }
    };

    const handleDocumentMessage = (event) => {
      if (!event?.data) return;
      try {
        if (typeof event.data === 'string') {
          handleInbound(event.data);
        }
      } catch (_) {}
    };

    const handleWindowMessage = (event) => {
      if (!event?.data) return;
      // Messages forwarded from the native wrapper via loader.js come through window.postMessage.
      try {
        if (
          event.data &&
          event.data.__comp === true &&
          event.data.type === 'RN_MESSAGE'
        ) {
          if (typeof event.data.data === 'string') {
            handleInbound(event.data.data);
          }
          return;
        }
        if (typeof event.data === 'string') {
          handleInbound(event.data);
        }
      } catch (_) {}
    };

    document.addEventListener('message', handleDocumentMessage);
    window.addEventListener('message', handleWindowMessage);
    return () => {
      document.removeEventListener('message', handleDocumentMessage);
      window.removeEventListener('message', handleWindowMessage);
    };
  }, []);

  function uploadAudio(recorderAudioAsBlob, duration, targets) {
    const targetIds = targets ? targets.ids : conversationIdsRef.current;
    const targetId = targets ? targets.id : conversationIDRef.current;
    props.setStatus('Uploading Audio...');

    return new Promise((resolve, reject) => {
      //read content of files (Blobs) asynchronously
      let reader = new FileReader();
      let isLarge = recorderAudioAsBlob.size > MAX_UPLOAD_SIZE;
      //once content has been read

      reader.onload = (e) => {
        // Store the base64 URL that represents the URL of the recording audio
        let base64URL = e.target.result;

        // Transform the audio data as necessary
        let rawData = base64URL;

        const isMultiTarget = targetIds && targetIds.length > 1;

        let uploadCall = null;
        if (isMultiTarget) {
          uploadCall = apiClient.addConversationAudioMultiple(
            targetIds,
            rawData,
            duration,
            previousLastSpeakerRef.current,
          );
        } else if (isLarge) {
          uploadCall = apiClient.addConversationAudioLarge(
            targetId,
            rawData,
            duration,
          );
        } else {
          uploadCall = apiClient.addConversationAudio(
            targetId,
            rawData,
            duration,
          );
        }

        uploadCall
          .then((data) => {
            if (data['error'] != null) {
              console.log('Error uploading audio: ' + data['error']);
              console.log(rawData);
              reject(data['error']);
            } else {
              // On multi-target, store lastSpeaker for next chunk so BE can associate if name not said in chunk
              if (
                isMultiTarget &&
                data.lastSpeaker != null &&
                data.lastSpeaker !== ''
              ) {
                previousLastSpeakerRef.current = data.lastSpeaker;
              }
              // On successful upload, refresh the UI
              console.log('Uploaded audio successfully! (Refreshing now)');
              props.refresh();
              // props.setStatus('Audio Uploaded!');
              resolve();
            }
          })
          .catch((error) => {
            console.log('Error uploading audio: ' + error);
            console.log(rawData);
            reject(error); // on reject it will go to the catch block in the processQueue function
          })
          .finally(() => {
            // Set the uploading status to false, indicating the upload process is complete
            setIsUploading(false);
          });
      };

      reader.onerror = (error) => {
        console.error('Error reading blob:', error);
        reject(error); // on reject it will go to the catch block in the processQueue function
      };

      // Start reading the content of the audio blob as a base64 URL
      reader.readAsDataURL(recorderAudioAsBlob);
    });
  }

  // props.continueTimer: Resume continues the timer instead of restarting it,
  // carrying the time already recorded into this visit (or this same set of visits).
  const elapsedBaseRef = useRef({ key: null, ms: 0 });
  const recordStartRef = useRef(0);
  const timerIds = () =>
    conversationIdsRef.current.length
      ? conversationIdsRef.current
      : [conversationIDRef.current];

  /** Computes the elapsed recording time since the moment the function is called in the format h:m:s*/
  function setupElapsedTimeStatusPush() {
    const ids = timerIds();
    const key = [...ids].sort().join(',');
    const transcribedMs =
      ids.length === 1 ? (props.seconds_transcribed || 0) * 1000 : 0;
    const priorMs =
      elapsedBaseRef.current.key === key ? elapsedBaseRef.current.ms : 0;
    elapsedBaseRef.current = props.continueTimer
      ? { key, ms: Math.max(priorMs, transcribedMs) }
      : { key: null, ms: 0 };
    recordStartRef.current = new Date();
    const timerStart = new Date(
      recordStartRef.current.getTime() - elapsedBaseRef.current.ms,
    );
    setElapsedTime(computeElapsedTime(timerStart));

    //create an interval that compute & displays elapsed time, as well as, animate red dot - every second
    clearInterval(elapsedTimeTimerRef.current);
    elapsedTimeTimerRef.current = setInterval(() => {
      //compute the elapsed time every second
      setElapsedTime(computeElapsedTime(timerStart));
    }, 1000); //every second
  }

  useEffect(() => {
    if (isRecording) {
      setupElapsedTimeStatusPush();
    } else {
      if (recordStartRef.current && elapsedBaseRef.current.key !== null) {
        elapsedBaseRef.current.ms += new Date() - recordStartRef.current;
        recordStartRef.current = 0;
      }
      clearInterval(elapsedTimeTimerRef.current);
    }
  }, [isRecording]);

  useEffect(() => {
    // if not isRecording and not isUploading, we can allow navigation away from the page
    if (!(isRecording || isUploading)) {
      // Another visit may have taken over recording
      if (!activeRecorder) {
        window.onbeforeunload = undefined;
      }
      // Allow screen to sleep
      if (!released && released != undefined) {
        release();
      }
    }
  }, [isRecording, isUploading]);

  const startButton = () => {
    if (props.isDisabled) {
      return;
    }
    // Check if we have credit available
    if (!apiClient.actionAllowed()) {
      props.setStatus('No Credits Remaining');
      return;
    }
    setHasStartedRecording(true); // Mark that recording has started at least once
    setStartTime(new Date());
    stopRequestedRef.current = false;
    if (canSwitchRecordingVisits()) {
      // One recording at a time: stop the active one first so its audio still uploads
      if (activeRecorder && activeRecorder !== actionsRef) {
        activeRecorder.current.stopButton();
      }
      activeRecorder = actionsRef;
    }
    startAudioRecording();

    // prevent the screen from going to sleep
    // console.log("Requesting Screen not sleep.")
    request();

    // Block Navigation away from the page until uploaded
    window.onbeforeunload = () => true;
  };

  useEffect(() => {
    if (startTime) {
      // Start a timer to stop recording after 61 minutes
      const timer = setTimeout(
        () => {
          // Check if still recording to avoid stopping if recording already stopped
          if (isRecordingRef.current) {
            console.log('Stopping recording after 75 minutes');
            stopButton();
          }
        },
        75 * 60 * 1000,
      ); // 75 minutes

      // Cleanup function to clear the timer if component unmounts or start button is clicked again
      return () => clearTimeout(timer);
    }
  }, [startTime]);

  const stopButton = () => {
    stopRequestedRef.current = true;
    setIsRecording(false);
    if (activeRecorder === actionsRef) {
      activeRecorder = null;
    }
    if (platform === 'android' || platform === 'ios') {
      sendNativeMessage('stopRecording');
      return;
    }
    setIsUploading(true);
    stopAudioRecording();
  };

  // Update actionsRef with current handlers for external access (tools, etc)
  actionsRef.current = {
    startButton,
    stopButton,
  };

  // Programmatic control for parents that drive recording externally (e.g. the
  // MultiRecord++ auto-switcher). Additive and backward-compatible — call sites
  // that pass no ref are completely unaffected. No deps so the exposed handlers
  // always close over the latest props/state.
  useImperativeHandle(ref, () => ({
    start: startButton,
    stop: stopButton,
    isRecording: () => isRecordingRef.current,
    // Live input level, 0–1 on a -60..0 dB scale.
    getLevel: () => {
      const analyser = audioAnalyserRef.current;
      if (!analyser) return 0;
      const data = new Uint8Array(analyser.fftSize);
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const db = 20 * Math.log10(Math.sqrt(sum / data.length) || 1e-6);
      return Math.min(1, Math.max(0, (db + 60) / 60));
    },
    getMics: () => ({
      devices: micDevices,
      selectedId: effectiveMicId(selectedMicId, micDevices),
    }),
    refreshMics: refreshMicDevices,
    // Mid-recording, cut the chunk so the next one starts on the new mic.
    setMic: (id) => {
      selectedMicIdRef.current = id;
      setSelectedMicId(id);
      if (
        isRecordingRef.current &&
        platform !== 'android' &&
        platform !== 'ios'
      ) {
        sendChunk();
      }
    },
  }));

  // Register toggle recording tool
  useEffect(() => {
    const toggleRecordingTool = createToggleRecordingTool(() => {
      if (isRecordingRef.current) {
        actionsRef.current.stopButton();
      } else {
        actionsRef.current.startButton();
      }
    });
    apiClient.addTools(toggleRecordingTool);
    return () => {
      apiClient.removeTools(toggleRecordingTool.name);
    };
  }, []);

  /** Computes the elapsedTime since the moment the function is called in the format mm:ss or hh:mm:ss
   * @param {String} startTime - start time to compute the elapsed time since
   * @returns {String} elapsed time in mm:ss format or hh:mm:ss format, if elapsed hours are 0.
   */
  function computeElapsedTime(startTime) {
    //record end time
    let endTime = new Date();

    //time difference in ms
    let timeDiff = endTime - startTime;

    //convert time difference from ms to seconds
    timeDiff = timeDiff / 1000;

    //extract integer seconds that dont form a minute using %
    let seconds = Math.floor(timeDiff % 60); //ignoring uncomplete seconds (floor)

    //pad seconds with a zero if neccessary
    seconds = seconds < 10 ? '0' + seconds : seconds;

    //convert time difference from seconds to minutes using %
    timeDiff = Math.floor(timeDiff / 60);

    //extract integer minutes that don't form an hour using %
    let minutes = timeDiff % 60; //no need to floor possible incomplete minutes, becase they've been handled as seconds
    minutes = minutes < 10 ? '0' + minutes : minutes;

    //convert time difference from minutes to hours
    timeDiff = Math.floor(timeDiff / 60);

    //extract integer hours that don't form a day using %
    let hours = timeDiff % 24; //no need to floor possible incomplete hours, becase they've been handled as seconds

    //convert time difference from hours to days
    timeDiff = Math.floor(timeDiff / 24);

    // the rest of timeDiff is number of days
    let days = timeDiff; //add days to hours

    let totalHours = hours + days * 24;
    totalHours = totalHours < 10 ? '0' + totalHours : totalHours;

    if (totalHours === '00') {
      return minutes + ':' + seconds;
    } else {
      return totalHours + ':' + minutes + ':' + seconds;
    }
  }

  // Handle the case where the user did not click stop but the view changed...
  useEffect(() => {
    // console.log("Mounting AudioRecorder");
    return onUnmount;
  }, []);

  function onUnmount() {
    // console.log("Unmounting AudioRecorder");
    // Stop the recording if it is still going
    // console.log("isRecording: " + isRecording)
    // console.log("isRecordingRef.current: " + isRecordingRef.current)
    clearTimeout(retargetTimerRef.current);
    if (isRecordingRef.current) {
      console.log('Stopping recording due to unmount');
      stopButton();
    }
  }

  // Handle the case where the user clicked the new conversation button and we should stop the recording...
  useEffect(() => {
    if (hash != null && hash != '' && isRecording) {
      console.log('Stopping recording due to hash change');
      stopButton();
    }
  }, [hash]);

  const defaultMicLabel =
    micDevices.find((d) => d.deviceId === 'default')?.label ||
    'System default microphone';

  // Name of the mic the next recording will use, shown on the Record button.
  const getActiveMicLabel = () => {
    // No audio inputs at all — don't imply a default exists.
    if (micDevices.length === 0) {
      return 'No microphone found';
    }
    const micId = micIdFor(selectedMicId, micDevices);
    // Empty or 'default' selection means "follow the system default".
    if (!micId || micId === 'default') {
      return defaultMicLabel;
    }
    // A specific device is pinned; fall back to the default if it's gone.
    const selected = micDevices.find((d) => d.deviceId === micId);
    return selected?.label || defaultMicLabel;
  };
  const activeMicLabel = getActiveMicLabel();
  const activeMicButtonLabel = getMicButtonLabel(activeMicLabel);

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <div
        style={{
          display: 'flex',
          position: 'relative',
          borderRadius: '4px',
          overflow: 'visible',
        }}
        className="dropdown-container mic-split-group"
      >
        {props.icon ? (
          <span
            id={props.recordButtonId}
            className={`fa ${
              isRecording ? 'fa-microphone-slash' : 'fa-microphone'
            } fa-2x`}
            style={{
              color: props.isDisabled
                ? '#ccc'
                : isRecording
                  ? 'red'
                  : '#6bd888',
              cursor: props.isDisabled ? 'not-allowed' : 'pointer',
              pointerEvents: props.isDisabled ? 'none' : 'auto',
              opacity: props.isDisabled ? 0.5 : 1,
            }}
            title={
              props.isDisabled
                ? 'Please wait for current recording/upload to complete.'
                : isRecording
                  ? 'Stop Recording'
                  : 'Start Recording'
            }
            onClick={
              props.isDisabled
                ? undefined // Prevent action
                : isRecording
                  ? stopButton
                  : startButton
            }
          />
        ) : (
          <Button
            id={props.recordButtonId}
            color="primary"
            size="md"
            style={{
              color: isRecording ? 'red' : 'white',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              borderRadius: isMobile ? '4px' : '4px 0 0 4px',
              border: 'none',
              marginRight: '0',
              opacity: props.isDisabled ? 0.6 : 1,
            }}
            disabled={props.isDisabled}
            onClick={isRecording ? stopButton : startButton}
            title={
              isRecording
                ? 'Stop Recording'
                : hasStartedRecording
                  ? 'Resume Recording'
                  : 'Start Recording'
            }
          >
            {isRecording ? (
              <>
                <MicDropStatus
                  isConnected={isRecording}
                  wordChangeCount={wordChangeCount}
                  fromRecorder={true}
                  isSpeaking={isSpeaking}
                />
                <span style={{ marginLeft: '1px' }}>Stop</span>
              </>
            ) : (
              <>
                <span
                  className="fa fa-microphone fa-2x"
                  style={{ marginRight: '6px', flexShrink: 0 }}
                />
                {platform === 'ios' || platform === 'android' ? (
                  // Native records via the OS, not getUserMedia — the enumerated
                  // mic isn't reliably what's used, so just show the action.
                  hasStartedRecording ? (
                    resumeLabel
                  ) : (
                    startLabel
                  )
                ) : (
                  // Action label + active mic name stacked next to the icon. The
                  // fa-2x icon sets the button height, so two compact lines fit
                  // inside it without growing the button; the mic name is
                  // width-capped + ellipsis so a long name can't widen it either.
                  // Full name shows on hover.
                  <span
                    className="mic-label-stack"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      minWidth: 0,
                      width: '60px',
                      lineHeight: 1.05,
                      textAlign: 'left',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {hasStartedRecording ? resumeLabel : startLabel}
                    </span>
                    <span
                      title={activeMicLabel}
                      style={{
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '10px',
                        fontWeight: 400,
                        marginTop: '1px',
                      }}
                    >
                      {activeMicButtonLabel}
                    </span>
                  </span>
                )}
              </>
            )}
          </Button>
        )}
        {!isRecording && !isMobile && (
          <Button
            color="primary"
            size="med"
            style={{
              borderRadius: '0 4px 4px 0',
              padding: '0 8px',
              minWidth: 'auto',
              marginLeft: '0',
              border: 'none',
              borderLeft: '1px solid rgba(255,255,255,0.2)',
            }}
            onClick={() => {
              setShowDropdown(!showDropdown);
            }}
            disabled={props.isDisabled || isMobile}
          >
            <span className="fa fa-chevron-down" />
          </Button>
        )}
        {showDropdown && !isRecording && (
          <div
            className="dropdown-container"
            style={{
              position: 'absolute',
              top: '100%',
              left: '0',
              minWidth: '300px',
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              zIndex: 1000,
              marginTop: '2px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                padding: '8px 12px',
              }}
            >
              <div
                style={{
                  alignSelf: 'anchor-center',
                  fontSize: '11px',
                  color: '#666',
                  fontWeight: 'bold',
                }}
              >
                Select Microphone
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}
              >
                <select
                  value={selectedMicId}
                  onChange={(e) => setSelectedMicId(e.target.value)}
                  onFocus={refreshMicDevices}
                  disabled={isRecording || props.isDisabled}
                  style={{
                    padding: '6px 8px',
                    borderRadius: '6px',
                    border: '1px solid #ccc',
                    fontSize: '14px',
                    flexWrap: 'wrap',
                  }}
                >
                  <option value="">{defaultMicLabel}</option>
                  {micDevices
                    .filter((d) => d.deviceId !== 'default')
                    .map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
      <audio ref={audioElementRef} controls hidden>
        <source type="audio/webm" />
      </audio>
      <Modal isOpen={showModal} onClose={handleCancel} isCloseButton={false}>
        Having trouble accessing your mic. Please check your settings or network
        and restart the recording.
      </Modal>
    </div>
  );
});

export default AudioRecorder;
